import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { isUndefined } from 'vscode/vscode/vs/base/common/types';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Extensions } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { textmateColorGroupSchemaId, textmateColorsSchemaId } from './colorThemeSchema.js';
import { workbenchColorsSchemaId } from 'vscode/vscode/vs/platform/theme/common/colorUtils';
import 'vscode/vscode/vs/platform/theme/common/colors/baseColors';
import 'vscode/vscode/vs/platform/theme/common/colors/chartsColors';
import 'vscode/vscode/vs/platform/theme/common/colors/editorColors';
import 'vscode/vscode/vs/platform/theme/common/colors/inputColors';
import 'vscode/vscode/vs/platform/theme/common/colors/listColors';
import 'vscode/vscode/vs/platform/theme/common/colors/menuColors';
import 'vscode/vscode/vs/platform/theme/common/colors/minimapColors';
import 'vscode/vscode/vs/platform/theme/common/colors/miscColors';
import 'vscode/vscode/vs/platform/theme/common/colors/quickpickColors';
import 'vscode/vscode/vs/platform/theme/common/colors/searchColors';
import { tokenStylingSchemaId } from 'vscode/vscode/vs/platform/theme/common/tokenClassificationRegistry';
import { ThemeSettings, ThemeSettingDefaults } from 'vscode/vscode/vs/workbench/services/themes/common/workbenchThemeService';
import { isWeb } from 'vscode/vscode/vs/base/common/platform';
import { ColorScheme } from 'vscode/vscode/vs/platform/theme/common/theme';

const _moduleId = "vs/workbench/services/themes/common/themeConfiguration";
const configurationRegistry = ( (Registry.as(Extensions.Configuration)));
const colorThemeSettingEnum = [];
const colorThemeSettingEnumItemLabels = [];
const colorThemeSettingEnumDescriptions = [];
function formatSettingAsLink(str) {
    return `\`#${str}#\``;
}
const COLOR_THEME_CONFIGURATION_SETTINGS_TAG = 'colorThemeConfiguration';
const colorThemeSettingSchema = {
    type: 'string',
    markdownDescription: ( localizeWithPath(
        _moduleId,
        0,
        "Specifies the color theme used in the workbench when {0} is not enabled.",
        formatSettingAsLink(ThemeSettings.DETECT_COLOR_SCHEME)
    )),
    default: isWeb ? ThemeSettingDefaults.COLOR_THEME_LIGHT : ThemeSettingDefaults.COLOR_THEME_DARK,
    tags: [COLOR_THEME_CONFIGURATION_SETTINGS_TAG],
    enum: colorThemeSettingEnum,
    enumDescriptions: colorThemeSettingEnumDescriptions,
    enumItemLabels: colorThemeSettingEnumItemLabels,
    errorMessage: ( localizeWithPath(_moduleId, 1, "Theme is unknown or not installed.")),
};
const preferredDarkThemeSettingSchema = {
    type: 'string',
    markdownDescription: ( localizeWithPath(
        _moduleId,
        2,
        'Specifies the color theme when system color mode is dark and {0} is enabled.',
        formatSettingAsLink(ThemeSettings.DETECT_COLOR_SCHEME)
    )),
    default: ThemeSettingDefaults.COLOR_THEME_DARK,
    tags: [COLOR_THEME_CONFIGURATION_SETTINGS_TAG],
    enum: colorThemeSettingEnum,
    enumDescriptions: colorThemeSettingEnumDescriptions,
    enumItemLabels: colorThemeSettingEnumItemLabels,
    errorMessage: ( localizeWithPath(_moduleId, 1, "Theme is unknown or not installed.")),
};
const preferredLightThemeSettingSchema = {
    type: 'string',
    markdownDescription: ( localizeWithPath(
        _moduleId,
        3,
        'Specifies the color theme when system color mode is light and {0} is enabled.',
        formatSettingAsLink(ThemeSettings.DETECT_COLOR_SCHEME)
    )),
    default: ThemeSettingDefaults.COLOR_THEME_LIGHT,
    tags: [COLOR_THEME_CONFIGURATION_SETTINGS_TAG],
    enum: colorThemeSettingEnum,
    enumDescriptions: colorThemeSettingEnumDescriptions,
    enumItemLabels: colorThemeSettingEnumItemLabels,
    errorMessage: ( localizeWithPath(_moduleId, 1, "Theme is unknown or not installed.")),
};
const preferredHCDarkThemeSettingSchema = {
    type: 'string',
    markdownDescription: ( localizeWithPath(
        _moduleId,
        4,
        'Specifies the color theme when in high contrast dark mode and {0} is enabled.',
        formatSettingAsLink(ThemeSettings.DETECT_HC)
    )),
    default: ThemeSettingDefaults.COLOR_THEME_HC_DARK,
    tags: [COLOR_THEME_CONFIGURATION_SETTINGS_TAG],
    enum: colorThemeSettingEnum,
    enumDescriptions: colorThemeSettingEnumDescriptions,
    enumItemLabels: colorThemeSettingEnumItemLabels,
    errorMessage: ( localizeWithPath(_moduleId, 1, "Theme is unknown or not installed.")),
};
const preferredHCLightThemeSettingSchema = {
    type: 'string',
    markdownDescription: ( localizeWithPath(
        _moduleId,
        5,
        'Specifies the color theme when in high contrast light mode and {0} is enabled.',
        formatSettingAsLink(ThemeSettings.DETECT_HC)
    )),
    default: ThemeSettingDefaults.COLOR_THEME_HC_LIGHT,
    tags: [COLOR_THEME_CONFIGURATION_SETTINGS_TAG],
    enum: colorThemeSettingEnum,
    enumDescriptions: colorThemeSettingEnumDescriptions,
    enumItemLabels: colorThemeSettingEnumItemLabels,
    errorMessage: ( localizeWithPath(_moduleId, 1, "Theme is unknown or not installed.")),
};
const detectColorSchemeSettingSchema = {
    type: 'boolean',
    markdownDescription: ( localizeWithPath(
        _moduleId,
        6,
        'If enabled, will automatically select a color theme based on the system color mode. If the system color mode is dark, {0} is used, else {1}.',
        formatSettingAsLink(ThemeSettings.PREFERRED_DARK_THEME),
        formatSettingAsLink(ThemeSettings.PREFERRED_LIGHT_THEME)
    )),
    default: false,
    tags: [COLOR_THEME_CONFIGURATION_SETTINGS_TAG],
};
const colorCustomizationsSchema = {
    type: 'object',
    description: ( localizeWithPath(_moduleId, 7, "Overrides colors from the currently selected color theme.")),
    allOf: [{ $ref: workbenchColorsSchemaId }],
    default: {},
    defaultSnippets: [{
            body: {}
        }]
};
const fileIconThemeSettingSchema = {
    type: ['string', 'null'],
    default: ThemeSettingDefaults.FILE_ICON_THEME,
    description: ( localizeWithPath(
        _moduleId,
        8,
        "Specifies the file icon theme used in the workbench or 'null' to not show any file icons."
    )),
    enum: [null],
    enumItemLabels: [( localizeWithPath(_moduleId, 9, 'None'))],
    enumDescriptions: [( localizeWithPath(_moduleId, 10, 'No file icons'))],
    errorMessage: ( localizeWithPath(_moduleId, 11, "File icon theme is unknown or not installed."))
};
const productIconThemeSettingSchema = {
    type: ['string', 'null'],
    default: ThemeSettingDefaults.PRODUCT_ICON_THEME,
    description: ( localizeWithPath(_moduleId, 12, "Specifies the product icon theme used.")),
    enum: [ThemeSettingDefaults.PRODUCT_ICON_THEME],
    enumItemLabels: [( localizeWithPath(_moduleId, 13, 'Default'))],
    enumDescriptions: [( localizeWithPath(_moduleId, 14, 'Default'))],
    errorMessage: ( localizeWithPath(_moduleId, 15, "Product icon theme is unknown or not installed."))
};
const detectHCSchemeSettingSchema = {
    type: 'boolean',
    default: true,
    markdownDescription: ( localizeWithPath(
        _moduleId,
        16,
        "If enabled, will automatically change to high contrast theme if the OS is using a high contrast theme. The high contrast theme to use is specified by {0} and {1}.",
        formatSettingAsLink(ThemeSettings.PREFERRED_HC_DARK_THEME),
        formatSettingAsLink(ThemeSettings.PREFERRED_HC_LIGHT_THEME)
    )),
    scope: 1 ,
    tags: [COLOR_THEME_CONFIGURATION_SETTINGS_TAG],
};
const themeSettingsConfiguration = {
    id: 'workbench',
    order: 7.1,
    type: 'object',
    properties: {
        [ThemeSettings.COLOR_THEME]: colorThemeSettingSchema,
        [ThemeSettings.PREFERRED_DARK_THEME]: preferredDarkThemeSettingSchema,
        [ThemeSettings.PREFERRED_LIGHT_THEME]: preferredLightThemeSettingSchema,
        [ThemeSettings.PREFERRED_HC_DARK_THEME]: preferredHCDarkThemeSettingSchema,
        [ThemeSettings.PREFERRED_HC_LIGHT_THEME]: preferredHCLightThemeSettingSchema,
        [ThemeSettings.FILE_ICON_THEME]: fileIconThemeSettingSchema,
        [ThemeSettings.COLOR_CUSTOMIZATIONS]: colorCustomizationsSchema,
        [ThemeSettings.PRODUCT_ICON_THEME]: productIconThemeSettingSchema
    }
};
configurationRegistry.registerConfiguration(themeSettingsConfiguration);
const themeSettingsWindowConfiguration = {
    id: 'window',
    order: 8.1,
    type: 'object',
    properties: {
        [ThemeSettings.DETECT_HC]: detectHCSchemeSettingSchema,
        [ThemeSettings.DETECT_COLOR_SCHEME]: detectColorSchemeSettingSchema,
    }
};
configurationRegistry.registerConfiguration(themeSettingsWindowConfiguration);
function tokenGroupSettings(description) {
    return {
        description,
        $ref: textmateColorGroupSchemaId
    };
}
const themeSpecificSettingKey = '^\\[[^\\]]*(\\]\\s*\\[[^\\]]*)*\\]$';
const tokenColorSchema = {
    type: 'object',
    properties: {
        comments: tokenGroupSettings(( localizeWithPath(_moduleId, 17, "Sets the colors and styles for comments"))),
        strings: tokenGroupSettings(( localizeWithPath(_moduleId, 18, "Sets the colors and styles for strings literals."))),
        keywords: tokenGroupSettings(( localizeWithPath(_moduleId, 19, "Sets the colors and styles for keywords."))),
        numbers: tokenGroupSettings(( localizeWithPath(_moduleId, 20, "Sets the colors and styles for number literals."))),
        types: tokenGroupSettings(( localizeWithPath(
            _moduleId,
            21,
            "Sets the colors and styles for type declarations and references."
        ))),
        functions: tokenGroupSettings(( localizeWithPath(
            _moduleId,
            22,
            "Sets the colors and styles for functions declarations and references."
        ))),
        variables: tokenGroupSettings(( localizeWithPath(
            _moduleId,
            23,
            "Sets the colors and styles for variables declarations and references."
        ))),
        textMateRules: {
            description: ( localizeWithPath(
                _moduleId,
                24,
                'Sets colors and styles using textmate theming rules (advanced).'
            )),
            $ref: textmateColorsSchemaId
        },
        semanticHighlighting: {
            description: ( localizeWithPath(
                _moduleId,
                25,
                'Whether semantic highlighting should be enabled for this theme.'
            )),
            deprecationMessage: ( localizeWithPath(
                _moduleId,
                26,
                'Use `enabled` in `editor.semanticTokenColorCustomizations` setting instead.'
            )),
            markdownDeprecationMessage: ( localizeWithPath(
                _moduleId,
                27,
                'Use `enabled` in {0} setting instead.',
                formatSettingAsLink('editor.semanticTokenColorCustomizations')
            )),
            type: 'boolean'
        }
    },
    additionalProperties: false
};
const tokenColorCustomizationSchema = {
    description: ( localizeWithPath(
        _moduleId,
        28,
        "Overrides editor syntax colors and font style from the currently selected color theme."
    )),
    default: {},
    allOf: [{ ...tokenColorSchema, patternProperties: { '^\\[': {} } }]
};
const semanticTokenColorSchema = {
    type: 'object',
    properties: {
        enabled: {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                29,
                'Whether semantic highlighting is enabled or disabled for this theme'
            )),
            suggestSortText: '0_enabled'
        },
        rules: {
            $ref: tokenStylingSchemaId,
            description: ( localizeWithPath(_moduleId, 30, 'Semantic token styling rules for this theme.')),
            suggestSortText: '0_rules'
        }
    },
    additionalProperties: false
};
const semanticTokenColorCustomizationSchema = {
    description: ( localizeWithPath(
        _moduleId,
        31,
        "Overrides editor semantic token color and styles from the currently selected color theme."
    )),
    default: {},
    allOf: [{ ...semanticTokenColorSchema, patternProperties: { '^\\[': {} } }]
};
const tokenColorCustomizationConfiguration = {
    id: 'editor',
    order: 7.2,
    type: 'object',
    properties: {
        [ThemeSettings.TOKEN_COLOR_CUSTOMIZATIONS]: tokenColorCustomizationSchema,
        [ThemeSettings.SEMANTIC_TOKEN_COLOR_CUSTOMIZATIONS]: semanticTokenColorCustomizationSchema
    }
};
configurationRegistry.registerConfiguration(tokenColorCustomizationConfiguration);
function updateColorThemeConfigurationSchemas(themes) {
    themes.sort((a, b) => a.label.localeCompare(b.label));
    colorThemeSettingEnum.splice(0, colorThemeSettingEnum.length, ...( (themes.map(t => t.settingsId))));
    colorThemeSettingEnumDescriptions.splice(0, colorThemeSettingEnumDescriptions.length, ...( (themes.map(t => t.description || ''))));
    colorThemeSettingEnumItemLabels.splice(0, colorThemeSettingEnumItemLabels.length, ...( (themes.map(t => t.label || ''))));
    const themeSpecificWorkbenchColors = { properties: {} };
    const themeSpecificTokenColors = { properties: {} };
    const themeSpecificSemanticTokenColors = { properties: {} };
    const workbenchColors = { $ref: workbenchColorsSchemaId, additionalProperties: false };
    const tokenColors = { properties: tokenColorSchema.properties, additionalProperties: false };
    for (const t of themes) {
        const themeId = `[${t.settingsId}]`;
        themeSpecificWorkbenchColors.properties[themeId] = workbenchColors;
        themeSpecificTokenColors.properties[themeId] = tokenColors;
        themeSpecificSemanticTokenColors.properties[themeId] = semanticTokenColorSchema;
    }
    themeSpecificWorkbenchColors.patternProperties = { [themeSpecificSettingKey]: workbenchColors };
    themeSpecificTokenColors.patternProperties = { [themeSpecificSettingKey]: tokenColors };
    themeSpecificSemanticTokenColors.patternProperties = { [themeSpecificSettingKey]: semanticTokenColorSchema };
    colorCustomizationsSchema.allOf[1] = themeSpecificWorkbenchColors;
    tokenColorCustomizationSchema.allOf[1] = themeSpecificTokenColors;
    semanticTokenColorCustomizationSchema.allOf[1] = themeSpecificSemanticTokenColors;
    configurationRegistry.notifyConfigurationSchemaUpdated(themeSettingsConfiguration, tokenColorCustomizationConfiguration);
}
function updateFileIconThemeConfigurationSchemas(themes) {
    fileIconThemeSettingSchema.enum.splice(1, Number.MAX_VALUE, ...( (themes.map(t => t.settingsId))));
    fileIconThemeSettingSchema.enumItemLabels.splice(1, Number.MAX_VALUE, ...( (themes.map(t => t.label))));
    fileIconThemeSettingSchema.enumDescriptions.splice(1, Number.MAX_VALUE, ...( (themes.map(t => t.description || ''))));
    configurationRegistry.notifyConfigurationSchemaUpdated(themeSettingsConfiguration);
}
function updateProductIconThemeConfigurationSchemas(themes) {
    productIconThemeSettingSchema.enum.splice(1, Number.MAX_VALUE, ...( (themes.map(t => t.settingsId))));
    productIconThemeSettingSchema.enumItemLabels.splice(1, Number.MAX_VALUE, ...( (themes.map(t => t.label))));
    productIconThemeSettingSchema.enumDescriptions.splice(1, Number.MAX_VALUE, ...( (themes.map(t => t.description || ''))));
    configurationRegistry.notifyConfigurationSchemaUpdated(themeSettingsConfiguration);
}
const colorSchemeToPreferred = {
    [ColorScheme.DARK]: ThemeSettings.PREFERRED_DARK_THEME,
    [ColorScheme.LIGHT]: ThemeSettings.PREFERRED_LIGHT_THEME,
    [ColorScheme.HIGH_CONTRAST_DARK]: ThemeSettings.PREFERRED_HC_DARK_THEME,
    [ColorScheme.HIGH_CONTRAST_LIGHT]: ThemeSettings.PREFERRED_HC_LIGHT_THEME
};
class ThemeConfiguration {
    constructor(configurationService, hostColorService) {
        this.configurationService = configurationService;
        this.hostColorService = hostColorService;
    }
    get colorTheme() {
        return this.configurationService.getValue(this.getColorThemeSettingId());
    }
    get fileIconTheme() {
        return this.configurationService.getValue(ThemeSettings.FILE_ICON_THEME);
    }
    get productIconTheme() {
        return this.configurationService.getValue(ThemeSettings.PRODUCT_ICON_THEME);
    }
    get colorCustomizations() {
        return this.configurationService.getValue(ThemeSettings.COLOR_CUSTOMIZATIONS) || {};
    }
    get tokenColorCustomizations() {
        return this.configurationService.getValue(ThemeSettings.TOKEN_COLOR_CUSTOMIZATIONS) || {};
    }
    get semanticTokenColorCustomizations() {
        return this.configurationService.getValue(ThemeSettings.SEMANTIC_TOKEN_COLOR_CUSTOMIZATIONS);
    }
    getPreferredColorScheme() {
        if (this.configurationService.getValue(ThemeSettings.DETECT_HC) && this.hostColorService.highContrast) {
            return this.hostColorService.dark ? ColorScheme.HIGH_CONTRAST_DARK : ColorScheme.HIGH_CONTRAST_LIGHT;
        }
        if (this.configurationService.getValue(ThemeSettings.DETECT_COLOR_SCHEME)) {
            return this.hostColorService.dark ? ColorScheme.DARK : ColorScheme.LIGHT;
        }
        return undefined;
    }
    getColorThemeSettingId() {
        const preferredScheme = this.getPreferredColorScheme();
        return preferredScheme ? colorSchemeToPreferred[preferredScheme] : ThemeSettings.COLOR_THEME;
    }
    async setColorTheme(theme, settingsTarget) {
        await this.writeConfiguration(this.getColorThemeSettingId(), theme.settingsId, settingsTarget);
        return theme;
    }
    async setFileIconTheme(theme, settingsTarget) {
        await this.writeConfiguration(ThemeSettings.FILE_ICON_THEME, theme.settingsId, settingsTarget);
        return theme;
    }
    async setProductIconTheme(theme, settingsTarget) {
        await this.writeConfiguration(ThemeSettings.PRODUCT_ICON_THEME, theme.settingsId, settingsTarget);
        return theme;
    }
    isDefaultColorTheme() {
        const settings = this.configurationService.inspect(this.getColorThemeSettingId());
        return settings && settings.default?.value === settings.value;
    }
    findAutoConfigurationTarget(key) {
        const settings = this.configurationService.inspect(key);
        if (!isUndefined(settings.workspaceFolderValue)) {
            return 6 ;
        }
        else if (!isUndefined(settings.workspaceValue)) {
            return 5 ;
        }
        else if (!isUndefined(settings.userRemote)) {
            return 4 ;
        }
        return 2 ;
    }
    async writeConfiguration(key, value, settingsTarget) {
        if (settingsTarget === undefined || settingsTarget === 'preview') {
            return;
        }
        const settings = this.configurationService.inspect(key);
        if (settingsTarget === 'auto') {
            return this.configurationService.updateValue(key, value);
        }
        if (settingsTarget === 2 ) {
            if (value === settings.userValue) {
                return Promise.resolve(undefined);
            }
            else if (value === settings.defaultValue) {
                if (isUndefined(settings.userValue)) {
                    return Promise.resolve(undefined);
                }
                value = undefined;
            }
        }
        else if (settingsTarget === 5  || settingsTarget === 6  || settingsTarget === 4 ) {
            if (value === settings.value) {
                return Promise.resolve(undefined);
            }
        }
        return this.configurationService.updateValue(key, value, settingsTarget);
    }
}

export { COLOR_THEME_CONFIGURATION_SETTINGS_TAG, ThemeConfiguration, formatSettingAsLink, updateColorThemeConfigurationSchemas, updateFileIconThemeConfigurationSchemas, updateProductIconThemeConfigurationSchemas };
